// ─── Final Offer → QuotationDocumentData mapper ───────────────────────────────
// Same canonical path as PQ. Display-only reshaping of an immutable Backend
// snapshot. No pricing / FX / total recalculation.

import type { CustomerFinalOfferData } from '@/lib/quotationTypes';
import type { QuotationDocumentData } from '@/quotation/document/types';
import {
  normalizeDocumentLocale,
  toNumber,
  mapSeller,
  mapBuyer,
  mapItem,
  mapExchangeRate,
} from './shared';

export function mapFinalOfferToDocument(
  offer: CustomerFinalOfferData,
): QuotationDocumentData {
  // Core commercial data — fail loudly rather than render a broken document (§26).
  if (!offer.offerNumber) {
    throw new Error('Invalid final offer: missing offer number.');
  }
  if (!Array.isArray(offer.items)) {
    throw new Error('Invalid final offer: missing items.');
  }
  const total = toNumber(offer.totals?.finalTotal);
  if (total == null) {
    throw new Error('Invalid final offer: missing total.');
  }

  const currency = offer.currency?.code ?? 'USD';
  const commercial = offer.commercial ?? {};

  return {
    documentType: 'final_offer',
    documentLocale: normalizeDocumentLocale(offer.documentLocale),
    documentTemplateVersion: offer.pdfTemplateVersion ?? undefined,

    quotationNumber: offer.offerNumber,
    referenceNumber: offer.preliminaryQuoteNumber ?? undefined,

    issuedAt: offer.issuedAt ?? '',
    validUntil: commercial.validUntil ?? undefined,

    seller: mapSeller(offer.seller),
    buyer: mapBuyer(offer.buyer),

    items: offer.items.map(mapItem),

    currency,

    // FO totals: preliminaryQuoteTotal is the combined product + finishing base
    // (Backend labels it "Product / Finishing Amount"). finalTotal is authoritative.
    productAmount: toNumber(offer.totals?.preliminaryQuoteTotal),
    freightAmount: toNumber(commercial.freightAmount),
    customsClearanceAmount: toNumber(commercial.customsClearanceAmount),
    insuranceAmount: toNumber(commercial.insuranceAmount),
    total,

    exchangeRate: mapExchangeRate(offer.fx?.appliedFxRate),

    paymentTerms: commercial.paymentTerms ?? undefined,
    incoterm: commercial.incoterms ?? undefined,
    leadTime: commercial.leadTimeText ?? undefined,
    remarks: commercial.commercialNotes ?? undefined,

    specialRequirements: offer.specialRequirements ?? undefined,
  };
}